import React, { useState, useEffect } from 'react';
import { Form, Alert, Spinner } from 'react-bootstrap';
import FormModal from '../shared/FormModal';
import SearchableSelect from '../shared/SearchableSelect';
import { useAuth } from '../../hooks/useAuth';
import { API_BASE_URL } from '../../config/api';

export default function TaskFormModal({ show, onHide, onTaskCreated, initialData = null, isFloating = false }) {
  const initialFormState = {
    title: '',
    description: '',
    priority: 'medium',
    status: 'todo',
    assigned_agent_id: null,
    vehicle_id: null,
    customer_id: null,
    due_date: '',
    store_id: null
  };

  const [formData, setFormData] = useState(initialFormState);
  const [agents, setAgents] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState(null);

  const { apiCall, user } = useAuth();
  const isEditMode = !!initialData;

  // Load data when modal opens or user changes
  useEffect(() => {
    if (show && user) {
      loadFormData();

      // Populate form if editing
      if (initialData) {
        setFormData({
          title: initialData.title || '',
          description: initialData.description || '',
          priority: initialData.priority || 'medium',
          status: initialData.status || 'todo',
          assigned_agent_id: initialData.assigned_agent_id || null,
          vehicle_id: initialData.vehicle_id || null,
          customer_id: initialData.customer_id || null,
          due_date: initialData.due_date ? initialData.due_date.split('T')[0] : '',
          store_id: initialData.store_id || null
        });
      } else {
        setFormData(initialFormState);
      }
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, initialData, user]);

  // Auto-fill store when agent is selected
  useEffect(() => {
    if (formData.assigned_agent_id && agents.length > 0 && user?.role === 'admin') {
      const selectedAgent = agents.find(a => a.id === formData.assigned_agent_id);
      if (selectedAgent && selectedAgent.store_id) {
        setFormData(prev => ({
          ...prev,
          store_id: selectedAgent.store_id
        }));
      }
    }
  }, [formData.assigned_agent_id, agents, user]);

  const loadFormData = async () => {
    try {
      setLoadingData(true);
      console.log('[TaskFormModal] Loading data for user role:', user?.role);

      const promises = [
        apiCall(`${API_BASE_URL}/users`).catch((err) => {
          console.error('Failed to load users:', err);
          return { data: [] };
        }),
      ];

      if (user?.role === 'admin') {
        console.log('[TaskFormModal] User is admin, loading stores...');
        promises.push(
          apiCall(`${API_BASE_URL}/stores`).catch((err) => {
            console.error('Failed to load stores:', err);
            return { data: [] };
          })
        );
      } else {
        console.log('[TaskFormModal] User is not admin, skipping stores load');
      }

      const results = await Promise.all(promises);

      setAgents(results[0]?.data || []);

      if (user?.role === 'admin' && results[1]) {
        console.log('Stores loaded:', results[1]?.data);
        setStores(results[1]?.data || []);
      }
    } catch (error) {
      console.error('Failed to load form data:', error);
      setError('Failed to load form data. Please try again.');
    } finally {
      setLoadingData(false);
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.title.trim()) {
      setError('Task title is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Clean up data - remove null values
      const payload = {
        title: formData.title.trim(),
        description: formData.description?.trim() || null,
        priority: formData.priority,
        status: formData.status,
        assigned_agent_id: formData.assigned_agent_id || null,
        vehicle_id: formData.vehicle_id || null,
        customer_id: formData.customer_id || null,
        due_date: formData.due_date || null,
        store_id: formData.store_id || null
      };

      if (isEditMode) {
        await apiCall(`${API_BASE_URL}/tasks/${initialData.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload)
        });
      } else {
        await apiCall(`${API_BASE_URL}/tasks`, {
          method: 'POST',
          body: JSON.stringify(payload)
        });
      }

      // Reset form and close
      setFormData(initialFormState);
      onTaskCreated();
      onHide();
    } catch (err) {
      setError(err.message || 'Failed to save task');
    } finally {
      setLoading(false);
    }
  };

  const formContent = (
    <>
      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      {loadingData ? (
        <div className="text-center p-4">
          <Spinner animation="border" />
          <p className="mt-2 text-muted">Loading form data...</p>
        </div>
      ) : (
        <Form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
          {/* Title (required) */}
          <Form.Group className="mb-3">
            <Form.Label>Task Title *</Form.Label>
            <Form.Control
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              placeholder="e.g., Inspect brake pads on Honda Civic"
              required
            />
          </Form.Group>

          {/* Description */}
          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="Add task details..."
            />
          </Form.Group>

          {/* Priority and Status in a row */}
          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Priority</Form.Label>
                <Form.Select
                  value={formData.priority}
                  onChange={(e) => setFormData({...formData, priority: e.target.value})}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </Form.Select>
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                >
                  <option value="todo">To Do</option>
                  <option value="doing">In Progress</option>
                  <option value="done">Done</option>
                </Form.Select>
              </Form.Group>
            </div>
          </div>

          {/* Assign to Agent (optional) */}
          <SearchableSelect
            items={agents}
            selectedItemId={formData.assigned_agent_id}
            onSelectItem={(id) => setFormData({...formData, assigned_agent_id: id})}
            getItemLabel={(agent) => `${agent.name} (${agent.role}) #${agent.id}`}
            getItemDetails={(agent) => [agent.name, agent.role, agent.id, agent.email, agent.store_name]}
            placeholder="Search agents..."
            label="Assign To"
            required={false}
          />

          {/* Link to Customer (optional) */}
          <SearchableSelect
            selectedItemId={formData.customer_id}
            onSelectItem={(id) => setFormData({...formData, customer_id: id})}
            getItemLabel={(c) => `${c.first_name} ${c.last_name}`}
            onSearch={async (term) => {
              const params = new URLSearchParams({ search: term, limit: '20' });
              const res = await apiCall(`${API_BASE_URL}/customers?${params}`);
              return res?.data || [];
            }}
            onFetchSelected={async (id) => {
              const res = await apiCall(`${API_BASE_URL}/customers/${id}`);
              return res?.data;
            }}
            placeholder="Search customers..."
            label="Related Customer"
            required={false}
          />

          {/* Link to Vehicle (optional) */}
          <SearchableSelect
            selectedItemId={formData.vehicle_id}
            onSelectItem={(id) => setFormData({...formData, vehicle_id: id})}
            getItemLabel={(v) => `${v.year} ${v.make} ${v.model}`}
            onSearch={async (term) => {
              const params = new URLSearchParams({ search: term, limit: '20' });
              const res = await apiCall(`${API_BASE_URL}/vehicles?${params}`);
              return (res?.data || []).filter(v => v.status !== 'sold');
            }}
            onFetchSelected={async (id) => {
              const res = await apiCall(`${API_BASE_URL}/vehicles/${id}`);
              return res?.data;
            }}
            placeholder="Search vehicles..."
            label="Related Vehicle"
            required={false}
          />

          {/* Due Date */}
          <Form.Group className="mb-3">
            <Form.Label>Due Date</Form.Label>
            <Form.Control
              type="date"
              value={formData.due_date}
              onChange={(e) => setFormData({...formData, due_date: e.target.value})}
            />
          </Form.Group>

          {/* Store (admin only) */}
          {user?.role === 'admin' && (
            <Form.Group className="mb-3">
              <Form.Label>Store</Form.Label>
              <Form.Select
                value={formData.store_id || ''}
                onChange={(e) => setFormData({...formData, store_id: e.target.value ? parseInt(e.target.value) : null})}
              >
                <option value="">Select store (optional)</option>
                {stores.map(store => (
                  <option key={store.id} value={store.id}>{store.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
          )}

          {/* Submit buttons for floating mode */}
          {isFloating && (
            <div className="d-flex gap-2 mt-4">
              <button type="submit" className="btn btn-primary" disabled={loading || loadingData}>
                {loading ? 'Saving...' : (isEditMode ? 'Update Task' : 'Create Task')}
              </button>
              <button type="button" className="btn btn-secondary" onClick={onHide}>
                Cancel
              </button>
            </div>
          )}
        </Form>
      )}
    </>
  );

  // If floating mode, return just the form content
  if (isFloating) {
    return formContent;
  }

  // Otherwise, wrap in FormModal
  return (
    <FormModal
      show={show}
      onHide={onHide}
      title={isEditMode ? 'Edit Task' : 'Create New Task'}
      onSubmit={handleSubmit}
      submitText={isEditMode ? 'Update Task' : 'Create Task'}
      size="lg"
      disableSubmit={loading || loadingData}
    >
      {formContent}
    </FormModal>
  );
}
